/**
 * Form Validation Utilities for City Landing Pages
 *
 * Provides reusable validation functions for interest forms,
 * champion applications, and contact forms.
 */

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Email Validation
 * RFC 5322 compliant email validation
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Phone Validation
 * US phone number validation (supports multiple formats)
 */
export function validatePhone(phone: string): boolean {
  const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
}

/**
 * ZIP Code Validation
 * US ZIP code validation (5-digit or ZIP+4)
 */
export function validateZipCode(zip: string): boolean {
  const zipRegex = /^\d{5}(-\d{4})?$/;
  return zipRegex.test(zip);
}

/**
 * Required Field Validation
 */
export function validateRequired(value: string | undefined | null): boolean {
  return value !== undefined && value !== null && value.trim().length > 0;
}

/**
 * Min Length Validation
 */
export function validateMinLength(value: string, minLength: number): boolean {
  return value.length >= minLength;
}

/**
 * Max Length Validation
 */
export function validateMaxLength(value: string, maxLength: number): boolean {
  return value.length <= maxLength;
}

/**
 * Interest Form Validation
 * Validates complete interest form data
 */
export function validateInterestForm(formData: {
  name?: string;
  email?: string;
  phone?: string;
  zipCode?: string;
  interests?: string[];
  message?: string;
}): ValidationResult {
  const errors: Record<string, string> = {};

  // Name validation
  if (!validateRequired(formData.name)) {
    errors.name = 'Name is required';
  } else if (!validateMinLength(formData.name!, 2)) {
    errors.name = 'Name must be at least 2 characters';
  }

  // Email validation
  if (!validateRequired(formData.email)) {
    errors.email = 'Email is required';
  } else if (!validateEmail(formData.email!)) {
    errors.email = 'Invalid email format';
  }

  // Phone validation (optional)
  if (formData.phone && !validatePhone(formData.phone)) {
    errors.phone = 'Invalid phone number format';
  }

  // ZIP code validation (optional)
  if (formData.zipCode && !validateZipCode(formData.zipCode)) {
    errors.zipCode = 'Invalid ZIP code format';
  }

  // Interests validation (at least one required)
  if (!formData.interests || formData.interests.length === 0) {
    errors.interests = 'Please select at least one interest';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Champion Application Validation
 * Validates champion application form data
 */
export function validateChampionApplication(formData: {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  experience?: string;
  motivation?: string;
}): ValidationResult {
  const errors: Record<string, string> = {};

  // Name validation
  if (!validateRequired(formData.name)) {
    errors.name = 'Name is required';
  }

  // Email validation
  if (!validateRequired(formData.email)) {
    errors.email = 'Email is required';
  } else if (!validateEmail(formData.email!)) {
    errors.email = 'Invalid email format';
  }

  // Phone validation (required for champions)
  if (!validateRequired(formData.phone)) {
    errors.phone = 'Phone number is required';
  } else if (!validatePhone(formData.phone!)) {
    errors.phone = 'Invalid phone number format';
  }

  // Address validation
  if (!validateRequired(formData.address)) {
    errors.address = 'Address is required';
  }

  // ZIP code validation
  if (!validateRequired(formData.zipCode)) {
    errors.zipCode = 'ZIP code is required';
  } else if (!validateZipCode(formData.zipCode!)) {
    errors.zipCode = 'Invalid ZIP code format';
  }

  // Experience validation
  if (!validateRequired(formData.experience)) {
    errors.experience = 'Please describe your relevant experience';
  } else if (!validateMinLength(formData.experience!, 50)) {
    errors.experience = 'Please provide at least 50 characters';
  }

  // Motivation validation
  if (!validateRequired(formData.motivation)) {
    errors.motivation = 'Please explain your motivation';
  } else if (!validateMinLength(formData.motivation!, 50)) {
    errors.motivation = 'Please provide at least 50 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Sanitize Input
 * Removes potentially harmful characters from user input
 */
export function sanitizeInput(input: string): string {
  return input
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .trim();
}

/**
 * Format Phone Number
 * Formats phone number to (XXX) XXX-XXXX
 */
export function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

/**
 * Format ZIP Code
 * Formats ZIP code to XXXXX or XXXXX-XXXX
 */
export function formatZipCode(zip: string): string {
  const cleaned = zip.replace(/\D/g, '');
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5)}`;
  }
  return cleaned.slice(0, 5);
}
